import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'
import { Toaster } from 'sonner'
import { ConfirmProvider } from '@/components/ui/confirm'
import { TooltipProvider } from '@/components/ui/overlays'
import { ApiError } from '@/lib/api'
import { AuthProvider } from './auth'
import { PanelProvider } from './panel'
import { router } from './router'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      /**
       * Retrying a 401, 403 or 404 only delays the state the surface already
       * knows how to render. Everything else gets the default two attempts,
       * because a cold backend dropping one request is the common case.
       */
      retry: (failureCount, error) =>
        error instanceof ApiError && [401, 403, 404].includes(error.status)
          ? false
          : failureCount < 2,
    },
    mutations: { retry: false },
  },
})

/**
 * The rendering harness, above the auth provider. Development only.
 *
 * `AuthProvider` replaces the whole tree with a configuration error when no
 * Clerk key is set, which would defeat the one thing the harness is for: it
 * renders citations from fixtures and talks to nothing, so it should need no
 * key, no session and no backend. Mounting it above the provider is what makes
 * "check the citation rendering" a one-command job on a fresh checkout.
 *
 * `import.meta.env.DEV` is a compile-time constant, so this whole branch and
 * the component behind it leave a production build.
 */
const HARNESS_PATH = '/dev/citations'

function DevHarness() {
  const CitationsHarness = lazy(() =>
    import('@/routes/CitationsHarness').then((m) => ({ default: m.CitationsHarness }))
  )
  return (
    <TooltipProvider>
      <PanelProvider>
        <Suspense fallback={null}>
          <CitationsHarness />
        </Suspense>
      </PanelProvider>
    </TooltipProvider>
  )
}

/**
 * The session gate is NOT here.
 *
 * It wraps `AppShell` instead, one level down, because `/share/$token` is
 * outside the shell and must answer to a reader with no account at all.
 * Everything above the router is the part both audiences need.
 */
export function App() {
  if (import.meta.env.DEV && window.location.pathname === HARNESS_PATH) {
    return <DevHarness />
  }

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ConfirmProvider>
            <PanelProvider>
              <RouterProvider router={router} />
            </PanelProvider>
            <AppToaster />
          </ConfirmProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </AuthProvider>
  )
}

function AppToaster() {
  return (
    <Toaster
      theme="light"
      position="bottom-center"
      toastOptions={{
        style: {
          background: 'var(--color-surface-raised)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-fg)',
          fontSize: '13px',
        },
      }}
    />
  )
}
